import * as React from 'react'
import { resolveTenant } from '@/lib/tenant'
import { fetchSnapshot, type VendorSnapshot } from '@/lib/api'
import { MiniVendorPage } from '@/components/mini-vendor/MiniVendorPage'
import { PageSkeleton } from '@/components/ui/PageSkeleton'
import { lazyWithRetry } from '@/lib/lazyWithRetry'
import { detectSocialApp, type SocialApp } from '@/lib/utils'
import { AppStateProvider } from './AppState'
import { TierProvider } from './TierContext'
import { initAnalytics, pageView } from '@/lib/analytics'
import { initBackendTracking, trackPageView, trackTripView } from '@/lib/backendTracking'
import { useRoute } from '@/lib/router'

// Policy pages are visited by a small fraction of sessions — they (and their
// four template chromes) load as one on-demand chunk. Both lazy components
// resolve from the same module, so navigating between them is a single fetch.
const TermsPage = React.lazy(
  lazyWithRetry(() => import('@/components/pages/PolicyPage').then((m) => ({ default: m.TermsPage })), {
    reloadOnFailure: true,
  }),
)
const CancellationPolicyPage = React.lazy(
  lazyWithRetry(
    () => import('@/components/pages/PolicyPage').then((m) => ({ default: m.CancellationPolicyPage })),
    { reloadOnFailure: true },
  ),
)

// Reached only from an operator-shared link, so it must never ride in the main
// bundle every visitor downloads.
const BalancePayPage = React.lazy(
  lazyWithRetry(
    () => import('@/components/pages/BalancePayPage').then((m) => ({ default: m.BalancePayPage })),
    { reloadOnFailure: true },
  ),
)

export function App() {
  const route = useRoute()
  const [snap, setSnap] = React.useState<VendorSnapshot | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [tenant, setTenant] = React.useState<string>('')

  // Legacy hash URL shim — runs once on mount.
  // Converts old /#/trip/{slug} and /#/destination/{slug} links (already shared on
  // WhatsApp etc) to new real-path URLs. replaceState keeps Back working correctly;
  // dispatching popstate lets the pathname router pick up the change without a reload.
  React.useEffect(() => {
    const hash = window.location.hash
    if (!hash || hash === '#') return
    const fwdMatch = hash.match(/^#\/(trip|destination)\/(.+)$/)
    if (fwdMatch) {
      const [, type, slugPart] = fwdMatch
      const cleanSlug = slugPart.split('?')[0].split('#')[0]
      const newPath = `/${type}/${cleanSlug}${window.location.search}`
      window.history.replaceState(null, '', newPath)
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }, [])

  React.useEffect(() => {
    let cancelled = false
    const url = new URL(window.location.href)
    resolveTenant(window.location.hostname, url.searchParams).then(t => {
      if (cancelled) return
      setTenant(t)
      // Fetch vendor snapshot client-side
      fetchSnapshot(t).then(setSnap).catch(e => setError(String(e)))
    })
    return () => { cancelled = true }
  }, [])

  // Detect in-app social browser and tag the document
  React.useEffect(() => {
    const app: SocialApp = detectSocialApp()
    if (app && app !== 'unknown') {
      document.documentElement.setAttribute('data-social-app', app)
      console.info('[tripzlink] Opened inside social app:', app)
    }
  }, [])

  // Update document.title on client-side route changes.
  // (Initial title comes from middleware-injected <title>__SEO_TITLE__</title>.)
  React.useEffect(() => {
    if (!snap) return
    const baseName = snap.branding?.name || snap.slug
    if (route.type === 'trip') {
      const trip = snap.trips.find(t => t.slug === route.slug || t.id === route.slug)
      if (trip) {
        document.title = `${trip.title} — ${baseName}`
        return
      }
    }
    if (route.type === 'destination') {
      document.title = `${route.slug} trips — ${baseName}`
      return
    }
    if (route.type === 'about') {
      document.title = `About — ${baseName}`
      return
    }
    if (route.type === 'terms') {
      document.title = `Terms & Conditions — ${baseName}`
      return
    }
    if (route.type === 'cancellation-policy') {
      document.title = `Refund & Cancellation Policy — ${baseName}`
      return
    }
    document.title = baseName
  }, [snap, route])

  // Initialize analytics/tracking once snap+tenant are known — exactly once per session.
  React.useEffect(() => {
    if (!snap || !tenant) return
    initAnalytics({ measurementId: import.meta.env.VITE_GA_ID, tenant, vendorId: snap.slug })
    initBackendTracking({ operatorSlug: snap.slug })
  }, [snap, tenant])

  // Fire page view tracking on every route change (driven by route state, not hashchange).
  React.useEffect(() => {
    if (!snap || !tenant) return
    const pageType =
      route.type === 'trip' ? 'trip_detail'
      : route.type === 'destination' ? 'destination'
      : route.type === 'about' ? 'about'
      : route.type === 'pay' ? 'pay'
      : route.type === 'terms' ? 'terms'
      : route.type === 'cancellation-policy' ? 'cancellation_policy'
      : 'home'
    pageView({ page_type: pageType, page_path: window.location.pathname, page_title: document.title })
    if (route.type === 'trip') {
      trackTripView(route.slug)
    } else {
      trackPageView()
    }
  }, [snap, tenant, route])

  // The pay page renders BEFORE the snapshot gate below, and tolerates a null
  // snapshot. An operator can hold a booking without ever having published
  // their mini-site — and the snapshot is what carries branding, not what
  // authorises the payment. Gating on it turned a valid payment link into
  // "Failed to load vendor snapshot" (seen on staging: tara-voyages).
  //
  // Still waits for the fetch to SETTLE (loaded or failed) so branding doesn't
  // pop in a beat after the amount; it just no longer requires it to succeed.
  if (route.type === 'pay') {
    if (snap === null && error === null) return <PageSkeleton />
    return (
      <React.Suspense fallback={<PageSkeleton />}>
        <BalancePayPage snap={snap} token={route.token} tenant={tenant} />
      </React.Suspense>
    )
  }

  if (error) {
    return <div className="mx-auto max-w-md p-6 text-center text-sm text-red-600">
      <p>Failed to load vendor snapshot.</p>
      <p className="mt-2">{error}</p>
    </div>
  }

  if (!snap) {
    return <PageSkeleton />
  }

  return (
    <AppStateProvider tenant={tenant} socialApp={detectSocialApp()} snap={snap}>
      <TierProvider tierConfig={snap.tierConfig}>
        <React.Suspense fallback={<PageSkeleton />}>
          {route.type === 'terms' ? (
            <TermsPage snap={snap} />
          ) : route.type === 'cancellation-policy' ? (
            <CancellationPolicyPage snap={snap} />
          ) : (
            <MiniVendorPage snap={snap} />
          )}
        </React.Suspense>
      </TierProvider>
    </AppStateProvider>
  )
}
